import React, { useState, useEffect } from 'react';
import QRCodeService, { GeneratedQRCode } from '../../services/qrCodeService';
import QRCode from 'qrcode';
import {
  QrCodeIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Stack,
  Paper,
  Fade,
  Zoom,
} from '@mui/material';
import { color } from 'framer-motion';
import { printHTML } from '../../utils/printUtils';
import { generateQRLayout } from '../../utils/qrLayoutGenerator';

interface ItemQRCodeGeneratorProps {
  itemId: string;
  orderId: string;
  productId: string;
  itemType: string;
  itemName?: string;
  showQRCode?: boolean;
  onQRCodeGenerated?: (qrCode: GeneratedQRCode) => void;
  // Use existing QR code data if available
  existingQrCode?: string;
  existingQrImageUrl?: string;
  // Optional details for print view
  productName?: string;
  customerName?: string;
  width?: number | string;
  height?: number | string;
  color?: string;
  // New props for QR code data from API
  qrCodeData?: any;
  qrCodeImages?: { [key: string]: string };
  onClose: () => void;
}

const ItemQRCodeGenerator: React.FC<ItemQRCodeGeneratorProps> = ({
  itemId,
  orderId,
  productId,
  itemType,
  itemName,
  showQRCode = true,
  onQRCodeGenerated,
  existingQrCode,
  existingQrImageUrl,
  productName,
  customerName,
  width: propWidth,
  height: propHeight,
  color: propColor,
  qrCodeData,
  qrCodeImages = {},
  onClose
}) => {
  const [qrCode, setQrCode] = useState<GeneratedQRCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(showQRCode);

  useEffect(() => {
    console.log('color from ===', color)
    console.log('propColor from ===', propColor)
  }, [color, propColor])

  useEffect(() => {
    if (itemId && orderId && productId && itemType) {
      generateQRCode();
    }
  }, [itemId, orderId, productId, itemType, qrCodeData, qrCodeImages, existingQrCode]);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError(null);

      // If we have qrCodeData from API, use it (preferred)
      if (qrCodeData?.item?.qr_code) {
        console.log('Using QR code data from API:', qrCodeData.item.qr_code);

        // Use qrCodeImages if available, otherwise generate from qr_code string
        let qrImageUrl = qrCodeImages[itemId] || qrCodeData.item.qr_image_data || existingQrImageUrl;
        if (!qrImageUrl && qrCodeData.item.qr_code) {
          // Generate QR code image from the existing QR code string
          qrImageUrl = await QRCode.toDataURL(qrCodeData.item.qr_code, {
            width: 400,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            },
            errorCorrectionLevel: 'H'
          });
        }

        const generatedQR: GeneratedQRCode = {
          data: qrCodeData.item.qr_code,
          imageUrl: qrImageUrl || '',
          qrCode: qrCodeData.item.qr_code
        };

        setQrCode(generatedQR);

        if (onQRCodeGenerated) {
          onQRCodeGenerated(generatedQR);
        }
        return;
      }

      // If we have existing QR code data, use it directly
      if (existingQrCode) {
        console.log('Using existing QR code data:', existingQrCode);
        console.log('QR code format check - starts with "p |":', existingQrCode.startsWith('p |'));

        // Generate QR code image from existing data (should be "p | encrypted_item_id")
        let qrImageUrl = existingQrImageUrl || qrCodeImages[itemId];
        if (!qrImageUrl && existingQrCode) {
          // Generate QR code image from the existing QR code string
          qrImageUrl = await QRCode.toDataURL(existingQrCode, {
            width: 400,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            },
            errorCorrectionLevel: 'H'
          });
        }

        // Use the existing QR code data directly (should be "p | encrypted_item_id")
        const generatedQR: GeneratedQRCode = {
          data: existingQrCode, // This should be "p | encrypted_item_id"
          imageUrl: qrImageUrl || '',
          qrCode: existingQrCode // Use the same data for consistency
        };

        setQrCode(generatedQR);

        if (onQRCodeGenerated) {
          onQRCodeGenerated(generatedQR);
        }
      } else {
        // Generate new QR code using QRCodeService
        console.log('No existing QR code data, generating new one using QRCodeService');

        const generatedQR = await QRCodeService.generateItemQRCode(
          itemId,
          orderId,
          productId,
          itemType
        );

        setQrCode(generatedQR);

        if (onQRCodeGenerated) {
          onQRCodeGenerated(generatedQR);
        }
      }
    } catch (err) {
      setError('Failed to generate QR code');
      console.error('QR Code generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = async () => {
    if (!qrCode) return;

    try {
      const link = document.createElement('a');
      link.href = qrCode.imageUrl;
      link.download = `${qrCode.qrCode}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // const printQRCode = () => {
  //   if (!qrCode) return;

  //   const printWindow = window.open('', '_blank');
  //   if (printWindow) {
  //     // Resolve dimensions and color from props first, then fallbacks
  //     const resolvedWidth = propWidth ?? (undefined as any);
  //     const resolvedHeight = propHeight ?? (undefined as any);
  //     const resolvedColor = propColor ?? (undefined as any);

  //     printWindow.document.write(`
  //       <!DOCTYPE html>
  //       <html>
  //         <head>
  //           <title>QR Code - ${itemName || itemType}</title>
  //           <style>
  //             body { 
  //               font-family: Arial, sans-serif; 
  //               margin: 0; 
  //               padding: 0; 
  //               background: #fff; 
  //             }
  //             .page { 
  //               padding: 24px; 
  //             }
  //             .header { 
  //               text-align: center; 
  //               margin-bottom: 24px; 
  //               border-bottom: 2px solid #000; 
  //               padding-bottom: 16px; 
  //             }
  //             .content {
  //               display: grid;
  //               grid-template-columns: 1fr 1.2fr;
  //               gap: 24px;
  //               align-items: start;
  //             }
  //             .qr-wrap {
  //               display: flex;
  //               justify-content: center;
  //               align-items: center;
  //               border: 2px solid #000;
  //               padding: 16px;
  //             }
  //             .qr-code { max-width: 380px; max-height: 380px; width: 100%; height: auto; }
  //             .details {
  //               font-size: 14px;
  //               line-height: 1.5;
  //             }
  //             .details h2 { margin: 0 0 8px 0; font-size: 18px; }
  //             .row { margin: 6px 0; }
  //             .label { font-weight: 700; display: inline-block; min-width: 120px; }
  //             @media print {
  //               body { margin: 0; }
  //               .page { padding: 12mm; }
  //             }
  //           </style>
  //         </head>
  //         <body>
  //           <div class="page">
  //             <div class="header">
  //               <h1>Item QR Code</h1>
  //               <p>Generated: ${new Date().toLocaleString()}</p>
  //             </div>
  //             <div class="content">
  //               <div class="qr-wrap">
  //                 <img src="${qrCode.imageUrl}" alt="QR Code" class="qr-code" />
  //               </div>
  //               <div class="details">
  //                 <h2>${itemName || itemType}</h2>
  //                 <div class="row"><span class="label">Type:</span> ${itemType}</div>
  //                 <div class="row"><span class="label">Customer:</span> ${customerName || '-'}</div>
  //                 <div class="row"><span class="label">Product:</span> ${productName || productId}</div>
  //                 <div class="row"><span class="label">Dimensions:</span> ${
  //                   (resolvedWidth && resolvedHeight)
  //                     ? `${Math.round(Number(resolvedWidth))}x${Math.round(Number(resolvedHeight))}`
  //                     : '-'
  //                 }</div>
  //                 <div class="row"><span class="label">Color:</span> ${propColor || '-'}</div>
  //               </div>
  //             </div>
  //           </div>
  //         </body>
  //       </html>
  //     `);
  //     printWindow.document.close();
  //     printWindow.print();
  //   }
  // };

  const printQRCode = () => {
    if (!qrCode) return;

    // Use qrCodeData if available, otherwise format from props
    const orderData = qrCodeData?.order || {
      id: orderId,
      order_number: '',
      order_number_new: '',
      customer_name: customerName || ''
    };

    const itemData = qrCodeData?.item || {
      id: itemId,
      item_number: itemName || itemId,
      qr_code: qrCode.qrCode || existingQrCode || '',
      type: itemType,
      current_stage: '',
      status: '',
      specifications: {},
      width: propWidth,
      height: propHeight,
      color: propColor,
      product: {
        id: productId,
        name: productName || '',
        type: itemType,
        color: propColor || ''
      }
    };

    // Use qrCodeImages if available, otherwise use generated QR code image
    const printQRImages = qrCodeImages[itemId]
      ? { [itemId]: qrCodeImages[itemId] }
      : qrCode.imageUrl
        ? { [itemId]: qrCode.imageUrl }
        : {};

    const html = generateQRLayout({
      singleItem: itemData,
      qrCodeImages: printQRImages,
      qrData: qrCodeData || { order: orderData, item: itemData },
      order: orderData,
    });

    printHTML({
      title: `QR - ${itemData.item_number}`,
      content: html,
      singlePage: true, // Single item QR code should print on one page
    });
  };


  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        bgcolor: 'grey.50',
        borderRadius: 2,
        minHeight: 200
      }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Generating QR Code...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={generateQRCode}
          >
            Try Again
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!qrCode) {
    return null;
  }

  return (
    <Card sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ color: 'primary.main', fontSize: '1.25rem' }}>
              <QrCodeIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Item QR Code
            </Typography>
            {existingQrCode && (
              <Chip
                label="Valid QR Code"
                size="small"
                color="success"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: 'action.hover'
              }
            }}
          >
            ✕
          </IconButton>

        </Box>

        {/* QR Code Display */}
        {true ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* QR Code Image */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                  <Zoom in={true} timeout={600}>
                    <img
                      src={qrCode.imageUrl}
                      alt={`QR Code for ${itemType}`}
                      style={{
                        width: '200px',
                        height: '200px',
                        maxWidth: '100%',
                      }}
                    />
                  </Zoom>
                </Box>
              </Box>

              {/* Item Information */}
              <Box sx={{ textAlign: 'center' }}>

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  mt: 2
                }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" color="text.secondary">
                      Type:
                    </Typography>
                    <Chip
                      label={itemType}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" color="text.secondary">
                      Name:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {itemName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>

                <Button
                  onClick={printQRCode}
                  variant="outlined"
                  startIcon={<PrinterIcon className="h-4 w-4" />}
                  sx={{ flex: 1 }}
                >
                  Print
                </Button>
              </Box>

              {/* Usage Instructions */}

            </Stack>
          </Box>
        ) : (
          /* Collapsed View */
          <Box sx={{
            textAlign: 'center',
            py: 4,
            px: 3
          }}>
            <Box sx={{
              fontSize: '2rem',
              color: 'grey.400',
              mb: 1
            }}>
              <QrCodeIcon style={{ width: '2rem', height: '2rem' }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              QR Code hidden
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Click the eye icon to show
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ItemQRCodeGenerator;
